/**
 * mcp.ts — MCP Streamable-HTTP client for the 5 SAGE MCP Workers.
 *
 * Ported from apps/sage-companion/src/providers/McpClient.ts (the proven
 * handshake) with two platform-specific changes:
 *
 *  1. Transport: inside Tauri we use @tauri-apps/plugin-http `fetch` — the
 *     request goes out from Rust (reqwest), so NO browser Origin header is
 *     sent and the 2026-05-26 worker lockdown accepts us with just a bearer.
 *     In `npm run dev` (browser) we go through the dev-only Vite proxy at
 *     /mcp-dev/<service>. On the production web build (pages.dev) live data
 *     is unsupported — workers 403 browser Origins by design.
 *
 *  2. Errors are typed (`McpResult`) instead of silent nulls, so cards can
 *     say "paste your token in Settings" vs "timed out" vs "desktop-only".
 *
 * Handshake (Cloudflare McpAgent requires the full dance):
 *   1. POST {method:"initialize"}            → capture Mcp-Session-Id header
 *   2. POST {method:"notifications/initialized"}  (ack, non-fatal)
 *   3. POST {method:"tools/call"}            → response is SSE; parse `data:` lines
 */

import { getServiceToken, isTauri } from "./settings";

export type McpService = "core" | "business" | "financial" | "intel" | "personal" | "limitless";

export const MCP_SERVICES: McpService[] = ["core", "business", "financial", "intel", "personal", "limitless"];

export type McpFailReason =
  | "no-token"
  | "unauthorized"
  | "web-unsupported"
  | "timeout"
  | "network"
  | "tool-error";

export type McpResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: McpFailReason; detail?: string };

const SESSION_TTL_MS = 4 * 60 * 1000;
// id === null marks a sessionless server (e.g. sage-financial-mcp answers
// initialize with a plain JSON result and no Mcp-Session-Id header).
const sessions = new Map<string, { id: string | null; expiresAt: number }>();
let rpcId = 1;

function endpointFor(service: McpService): string | null {
  if (isTauri()) return `https://sage-${service}-mcp.ramirezconsulting-rg.workers.dev/mcp`;
  if (import.meta.env.DEV) return `/mcp-dev/${service}`;
  return null; // production web build — live data is desktop-only
}

function authHeaders(service: McpService): Record<string, string> {
  const token = getServiceToken(service);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

type RawResult = { res: Response } | { err: "timeout" | "network"; detail?: string };

/**
 * Tauri path: the `mcp_http_post` Rust command (reqwest) sends ONLY the headers
 * we specify — no Origin header at all. (tauri-plugin-http's fetch forwards
 * `Origin: http://tauri.localhost`, which the locked-down workers 403.)
 */
async function tauriRawFetch(url: string, init: RequestInit, timeoutMs: number): Promise<RawResult> {
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const out = await invoke<{ status: number; headers: Record<string, string>; body: string }>(
      "mcp_http_post",
      {
        url,
        headers: (init.headers as Record<string, string>) ?? {},
        body: typeof init.body === "string" ? init.body : "",
        timeoutMs,
      },
    );
    return { res: new Response(out.body, { status: out.status, headers: new Headers(out.headers) }) };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes("network:") && (msg.includes("timed out") || msg.includes("timeout")))
      return { err: "timeout" };
    return { err: "network", detail: msg };
  }
}

async function rawFetch(url: string, init: RequestInit, timeoutMs: number): Promise<RawResult> {
  if (isTauri()) return tauriRawFetch(url, init, timeoutMs);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await window.fetch(url, { ...init, signal: controller.signal });
    return { res };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes("abort")) return { err: "timeout" };
    return { err: "network", detail: msg };
  } finally {
    clearTimeout(timer);
  }
}

async function ensureSession(
  service: McpService,
  endpoint: string,
): Promise<{ id: string | null } | { reason: McpFailReason; detail?: string }> {
  const cached = sessions.get(endpoint);
  if (cached && cached.expiresAt > Date.now()) return { id: cached.id };

  const out = await rawFetch(
    endpoint,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
        ...authHeaders(service),
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: rpcId++,
        method: "initialize",
        params: {
          protocolVersion: "2025-03-26",
          capabilities: {},
          clientInfo: { name: "sage-platform", version: "0.2.0" },
        },
      }),
    },
    10_000,
  );
  if ("err" in out) return { reason: out.err, detail: out.detail };

  const { res } = out;
  if (res.status === 401 || res.status === 403) {
    const body = await res.text().catch(() => "");
    return { reason: "unauthorized", detail: `HTTP ${res.status} ${body.slice(0, 120)}` };
  }
  const sessionId = res.headers.get("mcp-session-id") ?? res.headers.get("Mcp-Session-Id");
  if (!sessionId) {
    // Sessionless server? Valid if the body is a JSON-RPC initialize result.
    const body = await res.text().catch(() => "");
    const parsed = parseSseResponse(body) as Record<string, unknown> | null;
    if (res.ok && parsed && (parsed as { result?: unknown }).result !== undefined) {
      sessions.set(endpoint, { id: null, expiresAt: Date.now() + SESSION_TTL_MS });
      return { id: null };
    }
    return { reason: "tool-error", detail: `init: no session id (HTTP ${res.status}) ${body.slice(0, 120)}` };
  }

  // Ack — failures are non-fatal.
  await rawFetch(
    endpoint,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
        "Mcp-Session-Id": sessionId,
        ...authHeaders(service),
      },
      body: JSON.stringify({ jsonrpc: "2.0", method: "notifications/initialized" }),
    },
    5_000,
  );

  sessions.set(endpoint, { id: sessionId, expiresAt: Date.now() + SESSION_TTL_MS });
  return { id: sessionId };
}

function parseSseResponse(text: string): unknown | null {
  for (const line of text.split("\n")) {
    if (line.startsWith("data: ")) {
      try {
        return JSON.parse(line.slice(6));
      } catch {
        continue;
      }
    }
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

interface McpRpcResult {
  content?: Array<{ type: string; text?: string }>;
  structuredContent?: unknown;
  isError?: boolean;
}

export async function mcpCallTool<T = unknown>(
  service: McpService,
  toolName: string,
  args: Record<string, unknown> = {},
  timeoutMs = 15_000,
): Promise<McpResult<T>> {
  const endpoint = endpointFor(service);
  if (!endpoint) return { ok: false, reason: "web-unsupported" };
  if (!getServiceToken(service)) return { ok: false, reason: "no-token" };

  const session = await ensureSession(service, endpoint);
  if (!("id" in session)) return { ok: false, reason: session.reason, detail: session.detail };

  const out = await rawFetch(
    endpoint,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
        ...(session.id ? { "Mcp-Session-Id": session.id } : {}),
        ...authHeaders(service),
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: rpcId++,
        method: "tools/call",
        params: { name: toolName, arguments: { compact: true, ...args } },
      }),
    },
    timeoutMs,
  );
  if ("err" in out) {
    sessions.delete(endpoint);
    return { ok: false, reason: out.err, detail: out.detail };
  }

  const { res } = out;
  if (!res.ok) {
    if (res.status === 400 || res.status === 404) sessions.delete(endpoint);
    if (res.status === 401 || res.status === 403)
      return { ok: false, reason: "unauthorized", detail: `HTTP ${res.status}` };
    return { ok: false, reason: "tool-error", detail: `HTTP ${res.status}` };
  }

  let rawText = "";
  try {
    rawText = await res.text();
  } catch (e) {
    return { ok: false, reason: "network", detail: `body read: ${String(e)}` };
  }
  const parsed = parseSseResponse(rawText) as Record<string, unknown> | null;
  if (!parsed) return { ok: false, reason: "tool-error", detail: "unparseable response" };

  const result = parsed.result as McpRpcResult | undefined;
  const error = parsed.error as { message?: string } | undefined;
  if (error) return { ok: false, reason: "tool-error", detail: error.message ?? "rpc error" };
  if (result?.isError) {
    const txt = result.content?.find((c) => c.type === "text")?.text;
    return { ok: false, reason: "tool-error", detail: (txt ?? "tool reported error").slice(0, 200) };
  }

  if (result?.structuredContent !== undefined) return { ok: true, data: result.structuredContent as T };
  const textContent = result?.content?.find((c) => c.type === "text")?.text;
  if (textContent === undefined) return { ok: false, reason: "tool-error", detail: "empty result" };
  try {
    return { ok: true, data: JSON.parse(textContent) as T };
  } catch {
    return { ok: true, data: textContent as unknown as T };
  }
}

export function clearMcpSessions(): void {
  sessions.clear();
}

/** Human strings for failure reasons — used by McpCard and the test panel. */
export function describeFailure(reason: McpFailReason, detail?: string): string {
  switch (reason) {
    case "no-token":
      return "No SAGE token — paste it in Settings → Connections.";
    case "unauthorized":
      return `Worker rejected the token${detail ? ` (${detail})` : ""}.`;
    case "web-unsupported":
      return "Live SAGE data requires the desktop app.";
    case "timeout":
      return "Timed out reaching the worker.";
    case "network":
      return `Network error${detail ? `: ${detail}` : ""}.`;
    case "tool-error":
      return `Worker error${detail ? `: ${detail}` : ""}.`;
  }
}
